import _ from 'lodash';
import { featureLabel, midSentence } from 'home/AdminDashboard/AlarmParameters/constants/alarmParameters.constants';

// Contract with the events service: each event item carries is_overall,
// trigger_parameter_type, amp_type and chart_feature. An older service sends
// none of them, and then the template-name maps below decide.
const LEGACY_AMP_TYPES = {
  vibration_warning: 'velocity',
  vibration_critical: 'velocity',
  vibration_acceleration_warning: 'acceleration',
  vibration_acceleration_critical: 'acceleration',
  vibration_demod_warning: 'demod',
  vibration_demod_critical: 'demod'
};

// The AI agent's names for the three historic measures (spectrum_flavour.py).
const LEGACY_VIOLATED_FEATURES = {
  vibration_warning: 'rms_velocity',
  vibration_critical: 'rms_velocity',
  vibration_acceleration_warning: 'rms_acceleration',
  vibration_acceleration_critical: 'rms_acceleration',
  vibration_demod_warning: 'demod_rms',
  vibration_demod_critical: 'demod_rms'
};

export const knowsOverall = activity => Boolean(activity) && typeof activity.is_overall === 'boolean';

export const isOverallAlarm = activity => knowsOverall(activity) && activity.is_overall;

export const alarmLevel = activity => (
  /_critical$/.test((activity && activity.event_type) || '') ? 'critical' : 'warning'
);

// The chart query an alarm opens with: the same amp_type and feature as the email link.
// Per field, because a replayed event can carry is_overall with a null amp_type.
export const chartLinkParams = (activity) => {
  if (!activity) return {};
  const ampType = activity.amp_type || LEGACY_AMP_TYPES[activity.event_type];
  if (!ampType) return {};
  const params = { amp_type: ampType };
  if (activity.chart_feature) params.feature = activity.chart_feature;

  return params;
};

export const chartLinkQuery = (activity) => {
  const params = chartLinkParams(activity);

  return Object.keys(params).map(key => `&${key}=${encodeURIComponent(params[key])}`).join('');
};

export const violatedFeature = (activity) => {
  if (!activity) return null;
  if (LEGACY_VIOLATED_FEATURES[activity.event_type]) return LEGACY_VIOLATED_FEATURES[activity.event_type];
  if (!isOverallAlarm(activity) || !activity.trigger_parameter_type) return null;

  return activity.amp_type === 'demod' ? 'demod_rms' : activity.trigger_parameter_type;
};

// A template with its own format keeps it; any other overall alarm gets the generic
// one, unless it has no measure to name, and then custom shows the template name.
export const activityFormatKey = (activity, formats) => {
  if (formats[activity.event_type]) return activity.event_type;

  return isOverallAlarm(activity) && activity.trigger_parameter_type ? 'overall_alarm' : 'custom';
};

export const overallMeasureLabel = activity => midSentence(
  featureLabel((activity && activity.trigger_parameter_type) || '')
);

// The system vibration templates; metadata.overall_alarm_templates (events
// service) adds every overall alarm, admin-created ones included.
const VIBRATION_ALARM_EVENT_TYPES = [
  'vibration_warning', 'vibration_critical',
  'vibration_acceleration_warning', 'vibration_acceleration_critical',
  'vibration_demod_warning', 'vibration_demod_critical',
  'vibration_envelope_alarm', 'vibration_acceleration_envelope_alarm'
];

const ALARM_COUNT_KEYS = [
  'vibration_warning', 'vibration_critical', 'rms_current', 'rms_current_critical',
  'temperature_warning', 'temperature_warning_critical', 'pressure_warning', 'pressure_critical',
  'ultrasound_warning', 'ultrasound_critical'
];

const overallTemplates = metadata => (metadata && metadata.overall_alarm_templates) || [];

const withNames = (names, extra) => names.concat(extra.filter(name => names.indexOf(name) === -1));

// Same set the plant summary email counts under "Vibration Alarms" and links here.
const VIBRATION_SENSOR_TAG_TYPES = ['vibration', 'magnetic_flux', 'ultrasound_vector'];

export const vibrationAlarmEventTypes = metadata => withNames(
  VIBRATION_ALARM_EVENT_TYPES,
  overallTemplates(metadata).filter(t => VIBRATION_SENSOR_TAG_TYPES.indexOf(t.tag_type) !== -1).map(t => t.name)
);

// The templates widen the Vibration Alarms filter only while nothing has replaced it.
export const widenedVibrationFilter = (eventTypes, metadata) => {
  const widened = vibrationAlarmEventTypes(metadata);
  if (!_.isEqual(eventTypes, VIBRATION_ALARM_EVENT_TYPES) || _.isEqual(widened, eventTypes)) return null;

  return widened;
};

export const overallAlarmCount = (eventTypeCount, metadata) => withNames(
  ALARM_COUNT_KEYS,
  overallTemplates(metadata).map(t => t.name)
).reduce((sum, name) => sum + ((eventTypeCount && eventTypeCount[name]) || 0), 0);
